import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { MarketId, resolveMarket } from '../../../../src/markets';
import { dictionaryFor, FALLBACK_LANGUAGE, isSupportedLanguage, LANGUAGES } from '../i18n/languages';
import { MarketService } from './market.service';

const STORAGE_KEY = 'language';

/** A language the shopper picked, and the country they were in when they did. */
export interface LanguageChoice {
   language: string;
   country: MarketId | null;
}

/**
 * The language to show.
 *
 * 1. A pick made in this same country holds. Picking English in Madrid sticks
 *    for as long as you're in Spain.
 * 2. Otherwise the country's own language, if we have it. This is the
 *    auto-switch: arrive in Spain and the app is in Spanish, even if you had
 *    picked Bulgarian back home. That pick is still remembered for Bulgaria.
 * 3. Otherwise the browser's languages, in its order of preference.
 * 4. Otherwise English.
 */
export function chooseLanguage(
   country: MarketId | undefined,
   browserLanguages: readonly string[],
   stored: LanguageChoice | null,
): string {
   if (stored && isSupportedLanguage(stored.language) && stored.country === (country ?? null)) {
      return stored.language;
   }
   if (country) {
      const local = resolveMarket(country).language;
      if (isSupportedLanguage(local)) return local;
   }
   for (const tag of browserLanguages) {
      const code = tag.split('-')[0].toLowerCase();
      if (isSupportedLanguage(code)) return code;
   }
   return FALLBACK_LANGUAGE;
}

function readChoice(): LanguageChoice | null {
   try {
      const parsed = JSON.parse(localStorage.getItem(STORAGE_KEY) ?? 'null');
      return parsed && typeof parsed.language === 'string' ? parsed : null;
   } catch {
      return null;
   }
}

function browserLanguages(): readonly string[] {
   try {
      return navigator.languages?.length ? navigator.languages : [navigator.language];
   } catch {
      return [];
   }
}

/** Registers every language with ngx-translate and decides which one is showing. */
@Injectable({ providedIn: 'root' })
export class LanguageService {
   readonly languages = LANGUAGES;

   constructor(private translate: TranslateService, private market: MarketService) {}

   /** Called once at startup. */
   init(): void {
      for (const language of LANGUAGES) {
         this.translate.setTranslation(language.code, dictionaryFor(language));
      }
      // Fallback for any key a language lacks; the Translations type makes
      // that a compile error, so this only matters for currency codes.
      this.translate.setDefaultLang(FALLBACK_LANGUAGE);
      this.apply(chooseLanguage(this.market.detected, browserLanguages(), readChoice()));
   }

   get current(): string {
      return this.translate.currentLang ?? FALLBACK_LANGUAGE;
   }

   /** The shopper picked a language: show it, and keep it for this country. */
   choose(code: string): void {
      if (!isSupportedLanguage(code)) return;
      const choice: LanguageChoice = { language: code, country: this.market.detected ?? null };
      try {
         localStorage.setItem(STORAGE_KEY, JSON.stringify(choice));
      } catch {
         // Storage blocked: the pick lasts for this page only.
      }
      this.apply(code);
   }

   private apply(code: string): void {
      this.translate.use(code);
      // Screen readers, hyphenation and the browser's translate prompt all read this.
      document.documentElement.lang = code;
   }
}
